import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Stack, Checkbox, FormControlLabel
} from '@mui/material';
import * as api from './accountsApi';
import type { Account } from './accountsApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';

interface AccountViewProps {
    accountId: number | null;
}

export function AccountView({ accountId }: AccountViewProps) {
    const { t } = useTranslation();
    const detail = useDetailView<Account>();

    const loadData = useCallback(async () => {
        await detail.withLoading(async () => {
            if (accountId === null) {
                const newAccount: Account = {
                    id: 0,
                    accountNumber: '',
                    accountName: '',
                    isRoundingAccount: false,
                };
                detail.setItem(newAccount);
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getAccount(accountId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [accountId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const isAccountNumberValid = (): boolean =>
        !!detail.item?.accountNumber && detail.item.accountNumber.length > 0;

    const save = async () => {
        if (!detail.item) return;
        await detail.withSaving(async () => {
            if (detail.isNew) {
                const response = await api.createAccount(detail.item!);
                detail.setItem(response);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            } else {
                const response = await api.updateAccount(detail.item!);
                detail.setItem(response);
                detail.setIsEditing(false);
            }
        });
    };

    const deleteAccount = async () => {
        if (!detail.item) return;
        if (!window.confirm(t('account.confirmDelete'))) return;
        await detail.withSaving(async () => {
            await api.deleteAccount(detail.item!.id);
            window.history.back();
        });
    };

    return (
        <DetailViewShell<Account>
            title={`${detail.item?.accountNumber ?? ''} ${detail.item?.accountName ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isAccountNumberValid()}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={deleteAccount}
            onClearError={() => detail.setError(null)}
        >
            {(account) => (<>
                <FormRow label={t('account.accountNumber')}>
                    {!detail.isNew && !detail.isEditing ? (
                        <Typography sx={sx13}>{account.accountNumber}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                            value={account.accountNumber}
                            onChange={e => detail.update('accountNumber', e.target.value.toUpperCase())}
                            error={!isAccountNumberValid()}
                        />
                    )}
                </FormRow>

                <FormRow label={t('account.accountName')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>{account.accountName}</Typography>
                    ) : (
                        <TextField
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 100 }}
                            value={account.accountName}
                            onChange={e => detail.update('accountName', e.target.value)}
                        />
                    )}
                </FormRow>

                <FormRow label={t('account.isRoundingAccount')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>
                            {account.isRoundingAccount ? t('common.true') : t('common.false')}
                        </Typography>
                    ) : (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={account.isRoundingAccount}
                                    onChange={e => detail.update('isRoundingAccount', e.target.checked)}
                                />
                            }
                            label=""
                        />
                    )}
                </FormRow>
            </>)}
        </DetailViewShell>
    );
}
